import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { sortBySearchMatch } from "@ckgo2/shared";
import type { Activity, ActivityType } from "@ckgo2/shared/types";
import {
  ACTIVITY_TYPE_LABELS,
  ACTIVITY_TYPES,
  normalizeActivityTypes,
  TRIP_STATUSES,
} from "@ckgo2/shared/types";
import { enumLabel } from "@/i18n/enumLabels";
import { useFacetedParams } from "@/hooks/useFacetedParams";
import { useAdminInspirationList } from "@/hooks/useAdminLists";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel from "@/components/common/FacetPanel";
import ListSortSelect from "@/components/common/ListSortSelect";
import {
  countBuckets,
  matchesFacet,
  pageSlice,
  compareCzech,
  compareNewest,
  labelLookup,
} from "@/lib/clientFacets";
import { Plus, Activity as ActivityIcon, Edit, Trash2, Copy, MapPin, Clock } from "lucide-react";

const PAGE_SIZE = 50;
const DEFAULT_SORT = "recent";

/**
 * Seznam inspirací.
 *
 * Filtry, hledání i řazení žijí ve URL (`useFacetedParams`) — stejně jako u poptávek,
 * faktur nebo dodavatelů. Majitel si stěžoval, že se v obsahu špatně hledá a třídí:
 * dřív tu byla jen dvě rozbalovátka bez filtru na destinaci, takže garant jedné země
 * si svoje inspirace nemohl vyfiltrovat. Teď si vybere destinaci v panelu vlevo
 * a URL s tím výběrem si může uložit do záložek.
 */
/** Sloupec Typ: všechny typy inspirace jako štítky v pořadí výčtu; bez typu „-". */
function ActivityTypeChips({ types }: { types: readonly unknown[] | null | undefined }) {
  const list = normalizeActivityTypes(types);
  if (list.length === 0) return <span className="text-gray-400">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((type) => (
        <span
          key={type}
          className="bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded px-2 py-0.5 text-xs font-medium"
        >
          {enumLabel(ACTIVITY_TYPE_LABELS, type)}
        </span>
      ))}
    </div>
  );
}

export default function ActivitiesPage() {
  const { t } = useTranslation();
  const { getValues, search, page, params } = useFacetedParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Jediný seznam stránky, ve štíhlé projekci (sdílený dotaz s roletkami editorů).
  const { data, isLoading, isFetching, error } = useAdminInspirationList();

  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<Activity>>(`/api/admin/inspirations/${id}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      if (response.data?.id) {
        navigate(`/inspirations/${response.data.id}`);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/inspirations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(t("content.activities.list.confirmDelete", { title }))) {
      deleteMutation.mutate(id);
    }
  };

  const activities = useMemo(() => data?.data ?? [], [data]);

  // Popisky facety destinace skládá z řádků (štíhlý seznam nese `destinationName`), takže
  // stránka seznam destinací vůbec nestahuje.
  const destName = useMemo(
    () =>
      labelLookup(
        activities,
        (a) => a.destinationId,
        (a) => a.destinationName,
      ),
    [activities],
  );
  const sort = params.get("sort") ?? DEFAULT_SORT;
  const statusSel = getValues("status");
  const typeSel = getValues("activityType");
  const destSel = getValues("destinationId");

  const facets = useMemo(
    () => ({
      status: countBuckets(activities, (a) => a.status),
      activityTypes: countBuckets(activities, (a) => normalizeActivityTypes(a.activityTypes)),
      destinationId: countBuckets(activities, (a) => a.destinationId),
    }),
    [activities],
  );

  const filtered = useMemo(() => {
    const rows = activities.filter(
      (a) =>
        matchesFacet(statusSel, a.status) &&
        matchesFacet(typeSel, normalizeActivityTypes(a.activityTypes)) &&
        matchesFacet(destSel, a.destinationId),
    );
    return sortBySearchMatch(rows, search, {
      name: (a) => a.title,
      other: (a) => [a.location, a.slug, a.destinationName],
      tieBreak: (a, b) =>
        sort === "title"
          ? compareCzech(a.title, b.title)
          : compareNewest(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt),
    });
  }, [activities, search, statusSel, typeSel, destSel, sort]);

  const visible = pageSlice(filtered, page, PAGE_SIZE);

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        {t("content.activities.list.loadError")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.activities.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("content.activities.list.subtitle")}
          </p>
        </div>
        <Link to="/inspirations/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("content.activities.list.newInspiration")}
        </Link>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("content.activities.list.searchPlaceholder")}
        total={filtered.length}
        limit={PAGE_SIZE}
        isLoading={isLoading}
        isFetching={isFetching}
        actions={
          <ListSortSelect
            defaultValue={DEFAULT_SORT}
            options={[
              { value: "recent", label: t("shared.listSort.recent") },
              { value: "title", label: t("shared.listSort.titleAsc") },
            ]}
          />
        }
        filters={
          <>
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="status"
              buckets={facets.status}
              order={{ enum: TRIP_STATUSES }}
              labelFor={(v) =>
                v === "published"
                  ? t("common.status.published")
                  : v === "draft"
                    ? t("common.status.draft")
                    : t("common.status.archived")
              }
            />
            <FacetPanel
              title={t("common.fields.destination")}
              paramKey="destinationId"
              buckets={facets.destinationId}
              order="name"
              labelFor={destName}
              nullLabel={t("shared.listSort.withoutDestination")}
            />
            <FacetPanel
              title={t("common.fields.type")}
              paramKey="activityType"
              buckets={facets.activityTypes}
              order={{ enum: ACTIVITY_TYPES }}
              labelFor={(v) => enumLabel(ACTIVITY_TYPE_LABELS, v as ActivityType)}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {visible.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <ActivityIcon className="mb-2 h-12 w-12 text-gray-300" />
              <p>{t("content.activities.list.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("content.activities.list.thActivity")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.type")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.destination")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("content.activities.list.thDuration")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.status")}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visible.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {activity.featuredImage ? (
                          <img
                            src={activity.featuredImage}
                            alt={activity.title}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                            <ActivityIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            /{activity.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <ActivityTypeChips types={activity.activityTypes} />
                    </td>
                    <td className="px-6 py-4">
                      {activity.destinationName ? (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 dark:text-gray-300">
                            {activity.destinationName}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {activity.duration ? (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 dark:text-gray-300">
                            {activity.duration}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {activity.status === "published" ? (
                        <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                          {t("common.status.published")}
                        </span>
                      ) : activity.status === "draft" ? (
                        <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400">
                          {t("common.status.draft")}
                        </span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {t("common.status.archived")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => duplicateMutation.mutate(activity.id)}
                          disabled={duplicateMutation.isPending}
                          className="btn-ghost btn-sm"
                          title={t("common.actions.duplicate")}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/inspirations/${activity.id}`}
                          className="btn-ghost btn-sm"
                          title={t("common.actions.edit")}
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(activity.id, activity.title)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                          title={t("common.actions.delete")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
